import type { IEnrichableEntity, KeyedMarketDataSnapshot, Security } from '@talos/kyoko';
import { compact, values } from 'lodash-es';
import type { PriceGridBucket, PriceGridSourceItem } from './types';

export const PriceGridRowKind = {
  Parent: 'parent',
  Child: 'child',
} as const;
export type PriceGridRowKind = (typeof PriceGridRowKind)[keyof typeof PriceGridRowKind];

export interface PriceGridEntityParams {
  sourceItem: PriceGridSourceItem;
  bucket: PriceGridBucket;
  kind: PriceGridRowKind;
  rowID: string;
  dataPath: string[];
}

export class PriceGridEntity implements IEnrichableEntity<PriceGridEntity, KeyedMarketDataSnapshot> {
  readonly sourceItem: PriceGridSourceItem;
  readonly security: Security;
  readonly bucket: PriceGridBucket;
  readonly kind: PriceGridRowKind;
  readonly rowID: string;
  readonly dataPath: string[];
  marketData: { [key: string]: KeyedMarketDataSnapshot } = {};

  constructor({ sourceItem, bucket, kind, rowID, dataPath }: PriceGridEntityParams) {
    this.sourceItem = sourceItem;
    this.security = sourceItem.security;
    this.bucket = bucket;
    this.kind = kind;
    this.rowID = rowID;
    this.dataPath = dataPath;
  }

  get isParentRow() {
    return this.kind === PriceGridRowKind.Parent;
  }

  addEnrichments(enrichments: KeyedMarketDataSnapshot[]) {
    for (const enrichment of enrichments) {
      this.marketData[enrichment.requestKey] = enrichment;
    }
  }

  removeEnrichments(ids: string[]): void {
    for (const id of ids) {
      delete this.marketData[id];
    }
  }

  clearEnrichments(): void {
    this.marketData = {};
  }

  getEnrichments(): KeyedMarketDataSnapshot[] {
    return values(this.marketData);
  }

  getEnrichmentRequirements(): string[] {
    return compact([
      this.security.Symbol,
      this.security.MultilegDetails?.Legs?.at(0)?.Symbol,
      this.security.MultilegDetails?.Legs?.at(1)?.Symbol,
    ]);
  }

  hasEnrichment(id: string) {
    return id in this.marketData;
  }

  clone() {
    const clone = new PriceGridEntity({
      sourceItem: this.sourceItem,
      bucket: this.bucket,
      kind: this.kind,
      rowID: this.rowID,
      dataPath: this.dataPath,
    });
    clone.addEnrichments(this.getEnrichments());
    return clone;
  }
}
